import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import ReactApexChart from 'react-apexcharts';
import {
  AccountBalanceWalletOutlined,
  ReceiptLongOutlined,
  RequestQuoteOutlined,
  SellOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';

import API from '../data/api';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const integerFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatAmount = (value) => currencyFormatter.format(toNumber(value));
const formatCurrency = (value) => `BWP ${formatAmount(value)}`;
const formatCompactCurrency = (value) =>
  `BWP ${compactFormatter
    .format(toNumber(value))
    .replace(/([KMBT])$/, (_, suffix) => ` ${suffix.toLowerCase()}`)}`;
const formatPercentage = (value) => `${toNumber(value).toFixed(1)}%`;

const SUMMARY_CARD_CONFIG = [
  {
    key: 'transaction_count',
    label: 'Transactions',
    icon: ReceiptLongOutlined,
    format: 'integer',
    accent: '#1d4ed8',
  },
  {
    key: 'total_excl_amount',
    label: 'Total Excl Amount',
    icon: RequestQuoteOutlined,
    format: 'currency',
    accent: '#2563eb',
  },
  {
    key: 'total_vat_amount',
    label: 'Total VAT Amount',
    icon: SellOutlined,
    format: 'currency',
    accent: '#3b82f6',
  },
  {
    key: 'total_incl_amount',
    label: 'Total Incl Amount',
    icon: AccountBalanceWalletOutlined,
    format: 'currency',
    accent: '#0ea5e9',
  },
];

const FINANCIAL_MODULES = [
  { key: 'debtors', label: 'Debtors & Invoicing', disabled: false },
  { key: 'payroll', label: 'Payroll & Payslips', disabled: true },
  { key: 'treasury', label: 'Treasury', disabled: true },
  { key: 'controls', label: 'Ledger & Controls', disabled: true },
];

const DEFAULT_FINANCIAL_MODULE =
  FINANCIAL_MODULES.find((module) => !module.disabled)?.key || FINANCIAL_MODULES[0].key;
const FINANCIAL_ACTIVE_MODULE_STORAGE_KEY = 'depot_financial_active_module';

export default function DepotFinancialPage() {
  const [depots, setDepots] = useState([]);
  const [selectedDepot, setSelectedDepot] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeModule, setActiveModule] = useState(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_FINANCIAL_MODULE;
    }

    const savedModule = window.localStorage.getItem(FINANCIAL_ACTIVE_MODULE_STORAGE_KEY);
    const isValidSavedModule = FINANCIAL_MODULES.some(
      (module) => module.key === savedModule && !module.disabled,
    );
    return isValidSavedModule ? savedModule : DEFAULT_FINANCIAL_MODULE;
  });
  const [openPayslipDialog, setOpenPayslipDialog] = useState(false);

  const [summary, setSummary] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionRowsPerPage, setTransactionRowsPerPage] = useState(10);
  const [transactionTotalRows, setTransactionTotalRows] = useState(0);

  const fetchDepots = useCallback(async () => {
    try {
      const response = await API.get('depots/depots');
      const depotRows = Array.isArray(response.data) ? response.data : response.data?.depots || [];
      const normalizedDepots = depotRows.map((depot) => ({
        ...depot,
        depot_id: depot?.depot_id || depot?.id,
        depot_name: depot?.depot_name || depot?.name,
      }));
      setDepots(normalizedDepots);
    } catch (error) {
      console.error('Error fetching depots for financial tab:', error);
      setDepots([]);
    }
  }, []);

  const fetchDebtorTransactions = useCallback(async () => {
    if (!selectedDepot) {
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('depot_id', selectedDepot);
      if (startDate) {
        params.append('time_start', startDate);
      }
      if (endDate) {
        params.append('time_end', endDate);
      }
      params.append('page', String(transactionPage));
      params.append('records', String(transactionRowsPerPage));

      const response = await API.get(`depots/debtor-transactions/summary?${params.toString()}`);
      setSummary(response?.data?.summary || {});
      setAccounts(Array.isArray(response?.data?.accounts) ? response.data.accounts : []);
      const transactionRows = Array.isArray(response?.data?.rows) ? response.data.rows : [];
      setRows(transactionRows);
      setTransactionTotalRows(
        toNumber(
          response?.data?.total_records ||
            response?.data?.total_rows ||
            response?.data?.summary?.transaction_count ||
            transactionRows.length,
        ),
      );
    } catch (error) {
      console.error('Error fetching debtor transaction summary:', error);
      setSummary({});
      setAccounts([]);
      setRows([]);
      setTransactionTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [selectedDepot, startDate, endDate, transactionPage, transactionRowsPerPage]);

  useEffect(() => {
    fetchDepots();
  }, [fetchDepots]);

  useEffect(() => {
    const isDisabledModule = FINANCIAL_MODULES.some(
      (module) => module.key === activeModule && module.disabled,
    );
    if (isDisabledModule) {
      setActiveModule(DEFAULT_FINANCIAL_MODULE);
    }
  }, [activeModule]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(FINANCIAL_ACTIVE_MODULE_STORAGE_KEY, activeModule);
  }, [activeModule]);

  useEffect(() => {
    if (!selectedDepot && depots.length > 0) {
      setSelectedDepot(String(depots[0].depot_id));
    }
  }, [selectedDepot, depots]);

  useEffect(() => {
    setTransactionPage(1);
  }, [selectedDepot, startDate, endDate]);

  useEffect(() => {
    fetchDebtorTransactions();
  }, [fetchDebtorTransactions]);

  const summaryCards = useMemo(
    () =>
      SUMMARY_CARD_CONFIG.map((card) => {
        const value = summary?.[card.key];
        const formattedValue =
          card.format === 'currency'
            ? formatCurrency(value)
            : integerFormatter.format(toNumber(value));
        return {
          ...card,
          value: formattedValue,
        };
      }),
    [summary],
  );

  const accountChartRows = useMemo(() => {
    const sorted = [...accounts].sort(
      (left, right) => toNumber(right.total_incl_amount) - toNumber(left.total_incl_amount),
    );
    return sorted.slice(0, 8);
  }, [accounts]);

  const accountChartOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
      },
      plotOptions: {
        bar: {
          borderRadius: 6,
          horizontal: true,
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories: accountChartRows.map((item) => item.account_name || item.account_no || 'Unknown'),
        labels: {
          formatter: (value) => formatCompactCurrency(value),
        },
      },
      tooltip: {
        y: {
          formatter: (value) => `BWP ${currencyFormatter.format(value)}`,
        },
      },
      colors: ['#1d4ed8'],
      noData: {
        text: 'No account summary data for selected filters',
      },
    }),
    [accountChartRows],
  );

  const accountChartSeries = useMemo(
    () => [
      {
        name: 'Total Incl Amount',
        data: accountChartRows.map((item) => toNumber(item.total_incl_amount)),
      },
    ],
    [accountChartRows],
  );

  const payrollOverview = useMemo(
    () => ({
      totalStaff: 42,
      grossPayroll: 486250,
      deductions: 102340,
      netPayroll: 383910,
      pendingPayslips: 7,
      processedPayslips: 35,
    }),
    [],
  );

  const payrollCards = useMemo(
    () => [
      { key: 'staff', label: 'Total Staff', value: integerFormatter.format(payrollOverview.totalStaff), accent: '#1d4ed8' },
      { key: 'gross', label: 'Gross Payroll', value: formatCurrency(payrollOverview.grossPayroll), accent: '#2563eb' },
      { key: 'deductions', label: 'Deductions', value: formatCurrency(payrollOverview.deductions), accent: '#3b82f6' },
      { key: 'net', label: 'Net Payroll', value: formatCurrency(payrollOverview.netPayroll), accent: '#0ea5e9' },
    ],
    [payrollOverview],
  );

  const payrollTrendChartOptions = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        fontFamily: '"IBM Plex Sans", "Segoe UI", sans-serif',
      },
      stroke: { curve: 'smooth', width: 3 },
      xaxis: {
        categories: ['Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'],
      },
      yaxis: {
        labels: {
          formatter: (value) => formatCompactCurrency(value),
        },
      },
      legend: {
        position: 'top',
      },
      colors: ['#2563eb', '#0ea5e9'],
      tooltip: {
        y: {
          formatter: (value) => formatCurrency(value),
        },
      },
      noData: {
        text: 'No payroll trend data',
      },
    }),
    [],
  );

  const payrollTrendChartSeries = useMemo(
    () => [
      { name: 'Gross Payroll', data: [451320, 463000, 470850, 478630, 482120, 486250] },
      { name: 'Net Payroll', data: [355990, 362480, 369140, 374110, 379300, 383910] },
    ],
    [],
  );

  const payslipQueueRows = useMemo(
    () => [
      { employee: 'K. Tugwaro', department: 'Logistics', payPeriod: 'Mar 2026', gross: 18200, deductions: 2730, net: 15470, status: 'Pending' },
      { employee: 'A. Muyengwa', department: 'Logistics', payPeriod: 'Mar 2026', gross: 17750, deductions: 2662.5, net: 15087.5, status: 'Pending' },
      { employee: 'A. Chereka', department: 'Operations', payPeriod: 'Mar 2026', gross: 16900, deductions: 2535, net: 14365, status: 'Ready' },
      { employee: 'K. Ontong', department: 'Finance', payPeriod: 'Mar 2026', gross: 24500, deductions: 3675, net: 20825, status: 'Issued' },
      { employee: 'T. Motsamai', department: 'Forecourt', payPeriod: 'Mar 2026', gross: 11850, deductions: 1777.5, net: 10072.5, status: 'Ready' },
    ],
    [],
  );

  const treasuryCards = useMemo(
    () => [
      { key: 'bank_balance', label: 'Current Bank Balance', value: formatCurrency(1492350), accent: '#1d4ed8' },
      { key: 'payables_due', label: 'Payables Due (30d)', value: formatCurrency(312400), accent: '#2563eb' },
      { key: 'expected_receipts', label: 'Expected Receipts (30d)', value: formatCurrency(428500), accent: '#3b82f6' },
      { key: 'runway', label: 'Liquidity Cover', value: formatPercentage(76.4), accent: '#0ea5e9' },
    ],
    [],
  );

  const treasuryRows = useMemo(
    () => [
      { week: 'Week 1', opening: 1492350, inflows: 132000, outflows: 117450, closing: 1506900 },
      { week: 'Week 2', opening: 1506900, inflows: 128500, outflows: 141300, closing: 1494100 },
      { week: 'Week 3', opening: 1494100, inflows: 175000, outflows: 136250, closing: 1532850 },
      { week: 'Week 4', opening: 1532850, inflows: 121500, outflows: 160700, closing: 1493650 },
    ],
    [],
  );

  const controlsCards = useMemo(
    () => [
      { key: 'journals', label: 'Unposted Journals', value: integerFormatter.format(12), accent: '#1d4ed8' },
      { key: 'recons', label: 'Open Reconciliations', value: integerFormatter.format(4), accent: '#2563eb' },
      { key: 'audit_flags', label: 'Audit Flags', value: integerFormatter.format(3), accent: '#3b82f6' },
      { key: 'close_status', label: 'Month-End Close', value: '72% Complete', accent: '#0ea5e9' },
    ],
    [],
  );

  const controlsRows = useMemo(
    () => [
      { area: 'Bank Reconciliation', owner: 'Finance Manager', dueDate: '2026-03-24', status: 'In Progress' },
      { area: 'Debtor Age Analysis', owner: 'AR Clerk', dueDate: '2026-03-23', status: 'Pending' },
      { area: 'Payroll Journal Posting', owner: 'Payroll Officer', dueDate: '2026-03-22', status: 'Ready' },
      { area: 'VAT Schedule Review', owner: 'Financial Controller', dueDate: '2026-03-25', status: 'Blocked' },
      { area: 'General Ledger Close', owner: 'Finance Manager', dueDate: '2026-03-28', status: 'In Progress' },
    ],
    [],
  );

  const getStatusChipStyle = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'issued') {
      return { backgroundColor: '#dcfce7', color: '#166534' };
    }
    if (normalized === 'ready') {
      return { backgroundColor: '#dbeafe', color: '#1d4ed8' };
    }
    if (normalized === 'blocked') {
      return { backgroundColor: '#fee2e2', color: '#b91c1c' };
    }
    if (normalized === 'in progress') {
      return { backgroundColor: '#e0f2fe', color: '#0369a1' };
    }
    return { backgroundColor: '#fef3c7', color: '#92400e' };
  };

  return (
    <>
      <Helmet>
        <title> Financials </title>
      </Helmet>

      <Container maxWidth={false}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Financials
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Debtor transaction analytics foundation for invoices, reporting, payroll, and financial operations.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
          <TextField
            select
            size="small"
            label="Depot"
            value={selectedDepot}
            onChange={(event) => setSelectedDepot(event.target.value)}
            sx={{ minWidth: { xs: '100%', md: 260 } }}
          >
            {depots.map((depot) => (
              <MenuItem key={`financial-depot-${depot.depot_id}`} value={String(depot.depot_id)}>
                {depot.depot_name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            type="date"
            label="Start Date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { xs: '100%', md: 180 } }}
          />
          <TextField
            size="small"
            type="date"
            label="End Date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: startDate || undefined }}
            sx={{ minWidth: { xs: '100%', md: 180 } }}
          />
        </Stack>

        <Paper
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            mb: 3,
            backgroundColor: '#f8fafc',
          }}
        >
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ p: 1.25 }}>
            {FINANCIAL_MODULES.map((module) => {
              const isActive = activeModule === module.key;
              return (
                <Button
                  key={`financial-module-${module.key}`}
                  size="small"
                  disabled={module.disabled}
                  onClick={() => {
                    if (!module.disabled) {
                      setActiveModule(module.key);
                    }
                  }}
                  variant={isActive ? 'contained' : 'outlined'}
                  sx={{
                    borderRadius: 999,
                    textTransform: 'none',
                    px: 1.5,
                    py: 0.5,
                    minHeight: 34,
                    fontWeight: 700,
                    borderColor: module.disabled ? '#e2e8f0' : isActive ? '#2563eb' : '#cbd5e1',
                    color: module.disabled ? '#94a3b8' : isActive ? '#ffffff' : '#334155',
                    backgroundColor: module.disabled ? '#f8fafc' : isActive ? '#2563eb' : '#ffffff',
                    boxShadow: isActive ? '0 8px 16px rgba(37, 99, 235, 0.24)' : 'none',
                    '&:hover': {
                      borderColor: '#2563eb',
                      backgroundColor: isActive ? '#1d4ed8' : '#eff6ff',
                    },
                    '&.Mui-disabled': {
                      borderColor: '#e2e8f0',
                      color: '#94a3b8',
                      backgroundColor: '#f8fafc',
                    },
                  }}
                >
                  {module.label}
                </Button>
              );
            })}
          </Stack>
        </Paper>

        {activeModule === 'debtors' && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {summaryCards.map((card) => {
                const Icon = card.icon;
                return (
                  <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={`financial-card-${card.key}`}>
                    <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                      <CardContent>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Icon sx={{ color: card.accent }} fontSize="small" />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                          >
                            {card.label}
                          </Typography>
                        </Stack>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {card.value}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider', mb: 3 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Debtor Exposure by Account
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Top accounts by inclusive amount
                </Typography>
              </Stack>
              {loading ? (
                <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                  <CircularProgress size={26} />
                </Stack>
              ) : (
                <ReactApexChart
                  options={accountChartOptions}
                  series={accountChartSeries}
                  type="bar"
                  height={340}
                />
              )}
            </Paper>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, lg: 5 }}>
                <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Account Summary
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Account Name</TableCell>
                          <TableCell align="right">Transactions</TableCell>
                          <TableCell align="right">Excl Amount</TableCell>
                          <TableCell align="right">VAT</TableCell>
                          <TableCell align="right">Incl Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {accounts.map((account) => (
                          <TableRow key={`account-summary-${account.account_no}-${account.account_name}`}>
                            <TableCell>{account.account_name || account.account_no || '-'}</TableCell>
                            <TableCell align="right">
                              {integerFormatter.format(toNumber(account.transaction_count))}
                            </TableCell>
                            <TableCell align="right">{formatAmount(account.total_excl_amount)}</TableCell>
                            <TableCell align="right">{formatAmount(account.total_vat_amount)}</TableCell>
                            <TableCell align="right">{formatAmount(account.total_incl_amount)}</TableCell>
                          </TableRow>
                        ))}
                        {accounts.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              No account summary available.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, lg: 7 }}>
                <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Recent Debtor Transactions
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Batch #</TableCell>
                          <TableCell>Transaction Date</TableCell>
                          <TableCell>Account No</TableCell>
                          <TableCell>Account Name</TableCell>
                          <TableCell>Doc No</TableCell>
                          <TableCell align="right">Excl Amount</TableCell>
                          <TableCell align="right">Incl Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading && (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              <CircularProgress size={22} />
                            </TableCell>
                          </TableRow>
                        )}

                        {!loading &&
                          rows.map((row) => (
                            <TableRow key={`debtor-row-${row.batch_id}-${row.doc_no}-${row.trx_date}`}>
                              <TableCell>{row.batch_number || '-'}</TableCell>
                              <TableCell>{row.trx_date || row.batch_date || '-'}</TableCell>
                              <TableCell>{row.account_no || '-'}</TableCell>
                              <TableCell>{row.account_name || '-'}</TableCell>
                              <TableCell>{row.doc_no || '-'}</TableCell>
                              <TableCell align="right">{formatAmount(row.excl_amount)}</TableCell>
                              <TableCell align="right">{formatAmount(row.incl_amount)}</TableCell>
                            </TableRow>
                          ))}
                        {!loading && rows.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              No debtor transactions for current filters.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                  <TablePagination
                    component="div"
                    count={transactionTotalRows}
                    page={transactionPage - 1}
                    onPageChange={(_, newPage) => setTransactionPage(newPage + 1)}
                    rowsPerPage={transactionRowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setTransactionRowsPerPage(parseInt(event.target.value, 10));
                      setTransactionPage(1);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                </Paper>
              </Grid>
            </Grid>
          </>
        )}

        {activeModule === 'payroll' && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {payrollCards.map((card) => (
                <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={`payroll-card-${card.key}`}>
                  <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                    <CardContent>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                      >
                        {card.label}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: card.accent, mt: 0.8 }}>
                        {card.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, lg: 8 }}>
                <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Payroll Trend (Gross vs Net)
                  </Typography>
                  <ReactApexChart
                    options={payrollTrendChartOptions}
                    series={payrollTrendChartSeries}
                    type="line"
                    height={320}
                  />
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, lg: 4 }}>
                <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Payslip Processing Window
                  </Typography>
                  <Stack spacing={1}>
                    <Chip
                      label={`Pending Payslips: ${integerFormatter.format(payrollOverview.pendingPayslips)}`}
                      sx={{ backgroundColor: '#fef3c7', color: '#92400e' }}
                    />
                    <Chip
                      label={`Processed Payslips: ${integerFormatter.format(payrollOverview.processedPayslips)}`}
                      sx={{ backgroundColor: '#dcfce7', color: '#166534' }}
                    />
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Issue and track payslips, payroll journals, and disbursement readiness from this module.
                  </Typography>
                  <Button variant="contained" onClick={() => setOpenPayslipDialog(true)}>
                    Open Payslip Control Window
                  </Button>
                </Paper>
              </Grid>
            </Grid>

            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider', mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Payslip Issuance Queue
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Pay Period</TableCell>
                      <TableCell align="right">Gross</TableCell>
                      <TableCell align="right">Deductions</TableCell>
                      <TableCell align="right">Net</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payslipQueueRows.map((row) => (
                      <TableRow key={`payslip-row-${row.employee}-${row.payPeriod}`}>
                        <TableCell>{row.employee}</TableCell>
                        <TableCell>{row.department}</TableCell>
                        <TableCell>{row.payPeriod}</TableCell>
                        <TableCell align="right">{formatAmount(row.gross)}</TableCell>
                        <TableCell align="right">{formatAmount(row.deductions)}</TableCell>
                        <TableCell align="right">{formatAmount(row.net)}</TableCell>
                        <TableCell>
                          <Chip size="small" label={row.status} sx={getStatusChipStyle(row.status)} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </>
        )}

        {activeModule === 'treasury' && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {treasuryCards.map((card) => (
                <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={`treasury-card-${card.key}`}>
                  <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                    <CardContent>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                      >
                        {card.label}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: card.accent, mt: 0.8 }}>
                        {card.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                4-Week Treasury Forecast
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Week</TableCell>
                      <TableCell align="right">Opening</TableCell>
                      <TableCell align="right">Inflows</TableCell>
                      <TableCell align="right">Outflows</TableCell>
                      <TableCell align="right">Closing</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {treasuryRows.map((row) => (
                      <TableRow key={`treasury-row-${row.week}`}>
                        <TableCell>{row.week}</TableCell>
                        <TableCell align="right">{formatAmount(row.opening)}</TableCell>
                        <TableCell align="right">{formatAmount(row.inflows)}</TableCell>
                        <TableCell align="right">{formatAmount(row.outflows)}</TableCell>
                        <TableCell align="right">{formatAmount(row.closing)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </>
        )}

        {activeModule === 'controls' && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {controlsCards.map((card) => (
                <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={`controls-card-${card.key}`}>
                  <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                    <CardContent>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                      >
                        {card.label}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: card.accent, mt: 0.8 }}>
                        {card.value}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                Finance Control Checklist
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Control Area</TableCell>
                      <TableCell>Owner</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {controlsRows.map((row) => (
                      <TableRow key={`control-row-${row.area}`}>
                        <TableCell>{row.area}</TableCell>
                        <TableCell>{row.owner}</TableCell>
                        <TableCell>{row.dueDate}</TableCell>
                        <TableCell>
                          <Chip size="small" label={row.status} sx={getStatusChipStyle(row.status)} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </>
        )}

        <Dialog open={openPayslipDialog} onClose={() => setOpenPayslipDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Payslip Control Window</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This control window is ready for payslip issuing workflows: draft review, approval,
              secure delivery, and payroll journal posting.
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip
                label={`Pending Approval: ${integerFormatter.format(payrollOverview.pendingPayslips)}`}
                sx={{ backgroundColor: '#fef3c7', color: '#92400e' }}
              />
              <Chip
                label={`Ready to Issue: ${integerFormatter.format(3)}`}
                sx={{ backgroundColor: '#dbeafe', color: '#1d4ed8' }}
              />
              <Chip
                label={`Issued This Period: ${integerFormatter.format(payrollOverview.processedPayslips)}`}
                sx={{ backgroundColor: '#dcfce7', color: '#166534' }}
              />
            </Stack>
            <Typography variant="body2">
              Next implementation hooks: bulk issue endpoint, payslip template management, employee
              delivery log, and payroll audit trail.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPayslipDialog(false)}>Close</Button>
            <Button variant="contained" onClick={() => setOpenPayslipDialog(false)}>
              Continue
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
